import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../../../styles/dashboardTheme.css";
import "../../../../styles/managementTheme.css";
import "../styles/promociones.css";
import {
  /* descargarAsistenciaPromocion, */
  descargarInscritosPromocion,
  descargarInscritosExcel,
  listarHorariosInscritosPorPromocion,
  obtenerPromocion
} from "../../../../services/promocionesService";
import { obtenerFichaMatricula } from "../../../../services/matriculasService";
import { createRoot } from "react-dom/client";
import FichaInscritoContent from "../components/FichaInscritoContent";
import { showToast } from "../../../../utils/showToast";
import { usePermissions } from "../../../../utils/permissions";
import usePromocionInscritos from "../hooks/usePromocionInscritos";
import PromocionNotasTab from "../components/PromocionNotasTab";
import PromocionAsistenciaTab from "../components/PromocionAsistenciaTab";
import PromocionCertificadosTab from "../components/PromocionCertificadosTab";
import PromocionMencionesTab from "../components/PromocionMencionesTab";
import PromocionCronogramaView from "./PromocionCronogramaView.jsx";

const formatDate = (value) => {
  if (!value) return "-";
  if (typeof value === "string") {
    const datePart = value.split("T")[0].split(" ")[0];
    const matchIso = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (matchIso) {
      const [, year, month, day] = matchIso;
      return `${day}/${month}/${year}`;
    }
    const matchSlash = datePart.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (matchSlash) return datePart;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("es-CO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

/* const formatTime = (value) => (value ? String(value).slice(0, 5) : "");
 */
export default function PromocionInscritosView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const promocionId = id;

  const [promocion, setPromocion] = useState(null);
  const [promocionLoading, setPromocionLoading] = useState(false);
  const [promocionError, setPromocionError] = useState("");
  const [horarios, setHorarios] = useState([]);
  const [horariosLoading, setHorariosLoading] = useState(false);
  const [horariosError, setHorariosError] = useState("");
  const [horarioSeleccionadoId, setHorarioSeleccionadoId] = useState("");
  const [horarioFiltroId, setHorarioFiltroId] = useState("");
  const [inscritosError, setInscritosError] = useState("");
  const [exportingInscritos, setExportingInscritos] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const exportDropdownRef = useRef(null);
  /* const [exportingAsistencia, setExportingAsistencia] = useState(false); */
  const [notasDirty, setNotasDirty] = useState(false);
  const pipWindowRef = useRef(null);

  const abrirFicha = useCallback(async (matriculaId) => {
    let data;
    try {
      data = await obtenerFichaMatricula(matriculaId);
    } catch {
      showToast('No se pudo cargar la ficha del inscrito.', 'error');
      return;
    }

    // Intentar Document Picture-in-Picture (siempre encima, sin barra URL)
    if (window.documentPictureInPicture) {
      try {
        // Reutilizar ventana PiP si ya está abierta
        if (pipWindowRef.current && !pipWindowRef.current.closed) {
          pipWindowRef.current.close();
        }
        const pipWin = await window.documentPictureInPicture.requestWindow({
          width: 360,
          height: 300,
          disallowReturnToOpener: false,
        });
        pipWindowRef.current = pipWin;

        // Copiar estilos del documento principal al PiP
        [...document.styleSheets].forEach((sheet) => {
          try {
            const css = [...sheet.cssRules].map((r) => r.cssText).join('');
            const style = pipWin.document.createElement('style');
            style.textContent = css;
            pipWin.document.head.appendChild(style);
          } catch (_) { /* cross-origin sheets se ignoran */ }
        });

        const nombre = [data.apellidos, data.nombres].filter(Boolean).join(' ');
        pipWin.document.title = nombre || 'Ficha inscrito';

        // copyFn usa el navigator de la ventana PiP (que tiene el foco)
        const pipCopyFn = (text) => pipWin.navigator.clipboard.writeText(String(text || ''));

        const container = pipWin.document.createElement('div');
        pipWin.document.body.style.margin = '0';
        pipWin.document.body.appendChild(container);
        createRoot(container).render(<FichaInscritoContent data={data} copyFn={pipCopyFn} />);

        // Ajustar alto al contenido real tras el primer render
        setTimeout(() => {
          const h = pipWin.document.body.scrollHeight;
          if (h > 0) pipWin.resizeTo(360, Math.min(h + 2, pipWin.screen.availHeight - 80));
        }, 120);
        return;
      } catch (_) { /* navegador bloqueó PiP, caer en window.open */ }
    }

    // Fallback: popup normal
    window.open(
      `/ficha-inscrito/${matriculaId}`,
      `ficha_${matriculaId}`,
      'popup,width=380,height=440,resizable=yes,scrollbars=yes'
    );
  }, []);

  const { hasPermission } = usePermissions();
  const canViewInscritos = hasPermission("promociones.inscritos.listar");
  const canViewNotas = hasPermission("promociones.notas.listar");
  const canViewDiplomas = hasPermission("promociones.diplomas.listar");
  const canViewMenciones = hasPermission("promociones.diplomas.listar");
  const canViewAsistencia = hasPermission("promociones.asistencia.listar");
  const canViewCronograma = hasPermission("calendario.listar");
  const canLoadHorarios = canViewInscritos || canViewNotas;
  const getDefaultTab = useCallback(() => {
    if (canViewInscritos) return "listado";
    if (canViewAsistencia) return "asistencia";
    if (canViewNotas) return "notas";
    if (canViewDiplomas) return "certificados";
    if (canViewMenciones) return "menciones";
    if (canViewCronograma) return "cronograma";
    return "";
  }, [canViewInscritos, canViewAsistencia, canViewNotas, canViewDiplomas, canViewMenciones, canViewCronograma]);
  // El menu de 3 puntos de la lista de promociones puede pedir abrir directo en
  // "Cronograma" (navigate con state) en vez del tab por defecto.
  const [activeTab, setActiveTab] = useState(() => location.state?.tab || getDefaultTab() || "listado");

  const handleTabChange = useCallback(
    (nextTab) => {
      if (activeTab === "notas" && notasDirty) {
        const ok = window.confirm("Hay cambios sin guardar en Notas. Deseas descartarlos?");
        if (!ok) return;
        setNotasDirty(false);
      }
      setActiveTab(nextTab);
    },
    [activeTab, notasDirty]
  );

  const {
    inscritos,
    inscritosLoading,
    loadInscritos,
    resetInscritos,
  } = usePromocionInscritos();

  const promocionNombre = useMemo(() => {
    return promocion?.numero_promocion || promocion?.nombre || "Promocion";
  }, [promocion]);

  const cursoNombre = useMemo(() => {
    return promocion?.curso || promocion?.cursoNombre || "-";
  }, [promocion]);

  const codigoConvocatoria = useMemo(() => {
    const codigo = promocion?.codigo_convocatoria || promocion?.codigoConvocatoria;
    if (codigo) return codigo;
    if (promocion?.ciclo_codigo) {
      return `${promocion.ciclo_codigo}${promocion.ciclo_anio ? ` · ${promocion.ciclo_anio}` : ""}`;
    }
    return "-";
  }, [promocion]);

  const loadPromocion = useCallback(async () => {
    if (!promocionId) return;
    setPromocionLoading(true);
    setPromocionError("");
    try {
      const data = await obtenerPromocion(promocionId);
      setPromocion(data);
    } catch (err) {
      console.error(err);
      const message = err.message || "No se pudo cargar la promocion.";
      setPromocionError(message);
      showToast({ type: "danger", message });
    } finally {
      setPromocionLoading(false);
    }
  }, [promocionId]);

  const loadHorarios = useCallback(async () => {
    if (!promocionId || !canLoadHorarios) return;
    setHorariosLoading(true);
    setHorariosError("");
    try {
      const data = await listarHorariosInscritosPorPromocion(promocionId);
      const list = Array.isArray(data) ? data : [];
      setHorarios(list);
      if (!horarioSeleccionadoId && list.length) {
        setHorarioSeleccionadoId(String(list[0].id));
      }
      if (horarioSeleccionadoId && list.length) {
        const exists = list.some((h) => String(h.id) === String(horarioSeleccionadoId));
        if (!exists) {
          setHorarioSeleccionadoId(String(list[0].id));
        }
      }
    } catch (err) {
      console.error(err);
      const message = err.message || "No se pudieron cargar los horarios.";
      setHorariosError(message);
      showToast({ type: "danger", message });
    } finally {
      setHorariosLoading(false);
    }
  }, [promocionId, horarioSeleccionadoId, canLoadHorarios]);

  const loadInscritosData = useCallback(async () => {
    if (!promocionId || !canViewInscritos) return;
    setInscritosError("");
    try {
      await loadInscritos(promocionId);
    } catch (err) {
      const message = err.message || "No se pudieron cargar los inscritos.";
      setInscritosError(message);
    }
  }, [promocionId, loadInscritos, canViewInscritos]);

  const reloadAll = useCallback(() => {
    loadPromocion();
    if (canLoadHorarios) loadHorarios();
    if (canViewInscritos) loadInscritosData();
  }, [loadPromocion, loadHorarios, loadInscritosData, canLoadHorarios, canViewInscritos]);

  useEffect(() => {
    reloadAll();
    return () => resetInscritos();
  }, [reloadAll, resetInscritos]);

  useEffect(() => {
    const fallbackTab = getDefaultTab();
    if (!fallbackTab) return;
    const canStay =
      (activeTab === "listado" && canViewInscritos) ||
      (activeTab === "asistencia" && canViewAsistencia) ||
      (activeTab === "notas" && canViewNotas) ||
      (activeTab === "certificados" && canViewDiplomas) ||
      (activeTab === "menciones" && canViewMenciones) ||
      (activeTab === "cronograma" && canViewCronograma);
    if (!canStay) {
      setActiveTab(fallbackTab);
    }
  }, [activeTab, canViewInscritos, canViewAsistencia, canViewNotas, canViewDiplomas, canViewMenciones, canViewCronograma, getDefaultTab]);

  const handleExportInscritos = useCallback(async () => {
    if (!promocionId || !canViewInscritos) return;
    setExportingInscritos(true);
    try {
      await descargarInscritosPromocion(promocionId, promocionNombre);
      showToast({
        type: "success",
        message: "PDF de inscritos descargado correctamente.",
      });
    } catch (err) {
      console.error(err);
      showToast({
        type: "danger",
        message: err.message || "No se pudo descargar el PDF de inscritos.",
      });
    } finally {
      setExportingInscritos(false);
    }
  }, [promocionId, promocionNombre, canViewInscritos]);

  const handleExportExcel = useCallback(async () => {
    if (!promocionId || !canViewInscritos) return;
    setExportingExcel(true);
    try {
      await descargarInscritosExcel(promocionId, promocionNombre);
      showToast({ type: "success", message: "Excel de inscritos descargado correctamente." });
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo descargar el Excel de inscritos." });
    } finally {
      setExportingExcel(false);
    }
  }, [promocionId, promocionNombre, canViewInscritos]);

  useEffect(() => {
    if (!exportDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(e.target)) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [exportDropdownOpen]);

  /* const handleExportAsistencia = useCallback(async () => {
    if (!promocionId || !horarioSeleccionadoId) return;
    setExportingAsistencia(true);
    try {
      await descargarAsistenciaPromocion(promocionId, horarioSeleccionadoId, promocionNombre);
      showToast({
        type: "success",
        message: "PDF de asistencia descargado correctamente.",
      });
    } catch (err) {
      console.error(err);
      showToast({
        type: "danger",
        message: err.message || "No se pudo descargar el PDF de asistencia.",
      });
    } finally {
      setExportingAsistencia(false);
    }
  }, [promocionId, horarioSeleccionadoId, promocionNombre]); */

  const sortedInscritos = useMemo(() => {
    if (!Array.isArray(inscritos)) return [];
    return [...inscritos].sort((a, b) => {
      const apellidosA = String(a?.apellidos || "").trim();
      const apellidosB = String(b?.apellidos || "").trim();
      const apellidoCompare = apellidosA.localeCompare(apellidosB, "es", { sensitivity: "base" });
      if (apellidoCompare !== 0) return apellidoCompare;
      const nombresA = String(a?.nombres || "").trim();
      const nombresB = String(b?.nombres || "").trim();
      return nombresA.localeCompare(nombresB, "es", { sensitivity: "base" });
    });
  }, [inscritos]);

  const totalInscritos = canViewInscritos ? inscritos.length : 0;
  const loading = promocionLoading || (canViewInscritos ? inscritosLoading : false);
  const minRows = 10;
  const emptyRows = Math.max(0, minRows - inscritos.length);

  return (
    <section className="management-page management-theme-violet promociones-wrapper">
      <div className="management-card management-header mb-4">
        <div>
          <p className="management-eyebrow mb-1">CURSO: {cursoNombre}</p>
          <h4 className="management-title mb-1 fw-bold">PROMOCIÓN: {promocionNombre}</h4>
          <p className="text-muted mb-0">CONVOCATORIA: {codigoConvocatoria}</p>
        </div>
        <div className="d-flex flex-column align-items-end gap-2">
          <div className="d-flex gap-2 flex-wrap">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate("/dashboard/coordinacion/promociones")}
            >
              <i className="bi bi-arrow-left me-2" />
              Volver
            </button>
          </div>
          {canViewInscritos && <small className="text-muted">Total inscritos: {totalInscritos}</small>}
        </div>
      </div>

      {(promocionError || inscritosError || horariosError) && (
        <div className="alert alert-warning d-flex align-items-center justify-content-between management-alert mb-4">
          <div className="me-3">
            {promocionError && <p className="mb-1">Promocion: {promocionError}</p>}
            {horariosError && <p className="mb-1">Horarios: {horariosError}</p>}
            {inscritosError && <p className="mb-0">Estudiantes: {inscritosError}</p>}
          </div>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={reloadAll}>
            Reintentar
          </button>
        </div>
      )}

      <ul className="nav nav-tabs promociones-tabs mb-4">
        {canViewInscritos && (
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${activeTab === "listado" ? "active" : ""}`}
              onClick={() => handleTabChange("listado")}
            >
              Listado
            </button>
          </li>
        )}
        {canViewCronograma && (
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${activeTab === "cronograma" ? "active" : ""}`}
              onClick={() => handleTabChange("cronograma")}
            >
              Cronograma
              {promocion?.total_sesiones > 0 ? (
                <i className="bi bi-check-circle-fill text-success ms-1" title="Cronograma creado" />
              ) : (
                <i className="bi bi-exclamation-circle-fill text-warning ms-1" title="Sin cronograma creado" />
              )}
            </button>
          </li>
        )}
        {canViewAsistencia && (
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${activeTab === "asistencia" ? "active" : ""}`}
              onClick={() => handleTabChange("asistencia")}
            >
              Asistencia
            </button>
          </li>
        )}
        {canViewNotas && (
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${activeTab === "notas" ? "active" : ""}`}
              onClick={() => handleTabChange("notas")}
            >
              Notas
            </button>
          </li>
        )}
        {canViewDiplomas && (
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${activeTab === "certificados" ? "active" : ""}`}
              onClick={() => handleTabChange("certificados")}
            >
              Diplomas
            </button>
          </li>
        )}
        {canViewMenciones && (
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${activeTab === "menciones" ? "active" : ""}`}
              onClick={() => handleTabChange("menciones")}
            >
              Mencion de honor
            </button>
          </li>
        )}
      </ul>

      {activeTab === "listado" && canViewInscritos ? (
        <div className="management-card promociones-table-card">
          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-3">
            <div>
              <p className="management-label mb-1">Listado</p>
              <h5 className="mb-0">Estudiantes inscritos</h5>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="input-group input-group-sm">
                <span className="input-group-text">Horario</span>
                <select
                  className="form-select"
                  value={horarioSeleccionadoId}
                  onChange={(e) => setHorarioSeleccionadoId(e.target.value)}
                  disabled={horariosLoading || !horarios.length}
                >
                  {horarios.length === 0 && <option value="">Sin horarios</option>}
                  {horarios.map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.nombre}
                    </option>
                  ))}
                </select>
              </div>
              <div className="d-flex align-items-center gap-2 ms-auto">
                <div className="position-relative" ref={exportDropdownRef}>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm dropdown-toggle"
                    onClick={() => setExportDropdownOpen((o) => !o)}
                    disabled={!promocionId || exportingInscritos || exportingExcel || loading}
                  >
                    <i className="bi bi-download me-1" />
                    {(exportingInscritos || exportingExcel) ? "Exportando..." : "Exportar como"}
                  </button>
                  {exportDropdownOpen && (
                    <ul className="dropdown-menu show" style={{ top: "100%", right: 0, left: "auto", width: "max-content" }}>
                      <li>
                        <button
                          className="dropdown-item"
                          type="button"
                          onClick={() => { setExportDropdownOpen(false); handleExportInscritos(); }}
                          disabled={exportingInscritos}
                        >
                          <i className="bi bi-file-earmark-pdf text-danger me-2" />
                          PDF
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          type="button"
                          onClick={() => { setExportDropdownOpen(false); handleExportExcel(); }}
                          disabled={exportingExcel}
                        >
                          <i className="bi bi-file-earmark-excel text-success me-2" />
                          Excel
                        </button>
                      </li>
                    </ul>
                  )}
                </div>
                {/* <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={handleExportAsistencia}
                  disabled={!horarioSeleccionadoId || exportingAsistencia || horariosLoading}
                >
                  <i className="bi bi-file-earmark-pdf me-2" />
                  {exportingAsistencia ? "Exportando..." : "Listado asistencia"}
                </button> */}
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={reloadAll}
                  disabled={loading}
                >
                  <i className="bi bi-arrow-repeat me-2" />
                  Actualizar
                </button>
              </div>
            </div>
          </div>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
            </div>
          ) : (
            <div className="management-table-wrapper">
              <table className="table align-middle management-table promociones-table mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Matricula</th>
                    <th>Estudiante</th>
                    <th>Documento</th>
                    <th>Modalidad</th>
                    <th>Estado pago</th>
                    <th>Registro</th>
                    <th style={{ width: 48 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {inscritos.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center text-muted py-3">
                        No hay estudiantes inscritos en esta promocion.
                      </td>
                    </tr>
                  ) : (
                    sortedInscritos.map((item, idx) => (
                      <tr key={item.matricula_id || idx}>
                        <td>{idx + 1}</td>
                        <td>{item.codigo_matricula || "-"}</td>
                        <td
                          className="fw-semibold"
                          onClick={() => item.estudiante_id && navigate(`/dashboard/estudiantes/${item.estudiante_id}/detalle`)}
                          style={{ cursor: item.estudiante_id ? "pointer" : "default" }}
                          title={item.estudiante_id ? "Ver detalle del estudiante" : undefined}
                        >
                          {[item.apellidos, item.nombres].filter(Boolean).join(" ").trim()}
                        </td>
                        <td>{item.documento_identidad || "-"}</td>
                        <td>
                          <span
                            className={`badge rounded-pill px-3 py-2 ${
                              item.modalidad === "online"
                                ? "bg-info-subtle text-info-emphasis border border-info-subtle"
                                : "bg-light text-dark border"
                            }`}
                          >
                            {item.modalidad === "online" ? "Online" : "Presencial"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge rounded-pill px-3 py-2 ${
                              item.estado_pago === "pagado"
                                ? "bg-success-subtle text-success"
                                : item.estado_pago === "parcial"
                                  ? "bg-warning-subtle text-warning"
                                  : "bg-secondary-subtle text-muted"
                            }`}
                            style={(item.estado_pago === "pendiente" || item.estado_pago === "parcial") ? { cursor: "pointer" } : undefined}
                            title={(item.estado_pago === "pendiente" || item.estado_pago === "parcial") ? "Registrar pago" : undefined}
                            onClick={(item.estado_pago === "pendiente" || item.estado_pago === "parcial") ? (e) => {
                              e.stopPropagation();
                              navigate(`/dashboard/caja/pagos?matriculaId=${item.matricula_id}&estudianteId=${item.estudiante_id}`);
                            } : undefined}
                          >
                            {item.estado_pago || "pendiente"}
                          </span>
                        </td>
                        <td>{formatDate(item.fecha_matricula)}</td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-sm btn-link p-0"
                            title="Ver ficha"
                            style={{ color: '#6c757d' }}
                            onClick={() => abrirFicha(item.matricula_id)}
                          >
                            <i className="bi bi-card-list fs-5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                  {inscritos.length > 0 &&
                    emptyRows > 0 &&
                    Array.from({ length: emptyRows }).map((_, idx) => (
                      <tr key={`inscritos-empty-${idx}`} className="promociones-table-empty-row" aria-hidden="true">
                        <td colSpan={8}>&nbsp;</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : activeTab === "asistencia" && canViewAsistencia ? (
        <PromocionAsistenciaTab promocionId={promocionId} />
      ) : activeTab === "notas" && canViewNotas ? (
        <PromocionNotasTab
          promocionId={promocionId}
          horarios={horarios}
          horariosLoading={horariosLoading}
          horarioFiltroId={horarioFiltroId}
          setHorarioFiltroId={setHorarioFiltroId}
          onDirtyChange={setNotasDirty}
        />
      ) : activeTab === "certificados" && canViewDiplomas ? (
        <PromocionCertificadosTab promocionId={promocionId} />
      ) : activeTab === "menciones" && canViewMenciones ? (
        <PromocionMencionesTab promocionId={promocionId} />
      ) : activeTab === "cronograma" && canViewCronograma ? (
        <PromocionCronogramaView promocionId={promocionId} />
      ) : (
        <div className="alert alert-warning d-flex align-items-center management-alert mb-4">
          <div>No tienes permisos para ver contenido de esta promocion.</div>
        </div>
      )}

    </section>
  );
}
